import React from "react";
import { useNavigate } from "react-router-dom";
import { Image, Stack } from "react-bootstrap";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import "./TopupSuccessful.scss";
import HyperCoin from "../../../components/HyperCoin/HyperCoin";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import Gem from "../../../components/Gem/Gem";
import {
  ROUTES,
  usePlayGameMetaDescription,
} from "../../../utils/constants";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import { getInsuranceBonusFollowUp } from "../../../utils/storage";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../../utils/renderHeaders";
import { topupInfoAtom } from "../../../jotai/atoms/topup";
import ImageHprChest from "../../../images/hpr-chest.png";
import ImageHprGiftBox from "../../../images/hpr-gift-box.png";
import { formatHprBalance } from "../../../utils/helpers";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

export default function TopupSuccessful() {
  const navigate = useNavigate();
  const { goToStep } = useOnboardingSteps();
  const { t } = useTranslation("wallet");
  const topupInfo = useAtomValue(topupInfoAtom);
  const { amount, bonusHpr, gem } = topupInfo;
  const metaDescriptions = usePlayGameMetaDescription();
  const hasBonus = !!bonusHpr || !!gem;
  const hasHprAndGem = !!bonusHpr && !!gem;
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const displayAmount = isFiatDisplay
    ? formatHprToFiatValue(amount ?? 0)
    : (amount ?? 0);

  const displayBonusHpr = isFiatDisplay
    ? formatHprToFiatValue(bonusHpr ?? 0)
    : bonusHpr;

  const handleGoToGames = () => {
    const followUp = getInsuranceBonusFollowUp();
    if (followUp) {
      goToStep(followUp.step);
      navigate(followUp.path);
      return;
    }
    navigate(ROUTES.GAMES.ROOT);
  };

  return (
    <>
      {renderHeaders({
        title: t("metadata:successful_top_up_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:successful_top_up_hyper_gaming_app"),
        ),
      })}

      <div className="TopupSuccessful">
        <div className="d-flex flex-column justify-content-center align-items-center MainContent">
          {hasBonus ? (
            <Image
              src={ImageHprGiftBox}
              alt="HPR Gift Box"
              className="HprGiftBoxImage"
              width={117.5}
              height={110}
            />
          ) : (
            <div className="ChestImageContainer">
              <Image src={ImageHprChest} alt="Cash" className="ChestImage" />
            </div>
          )}
          <Stack gap={3} style={{ flex: 0 }}>
            <Stack gap={2} className="TopupDetails">
              <p className="MainCopy">
                {t("you_received")}{" "}
                <HyperCoin width={20} height={20} className="align-top" />
                {formatHprBalance(amount, 3)}!
              </p>
              <p className="SubCopy">
                <span className="TopupAmount">
                  {!isFiatDisplay && <HyperCoin width={14.5} height={14.5} />}
                  {displayAmount}
                </span>{" "}
                {t("topped_up_successfully")}
                {hasBonus ? ` ${t("you_also_received_a_bonus")}` : ""}{" "}
                {t("happy_gaming")}
              </p>
            </Stack>
            {hasBonus && (
              <Stack
                direction="horizontal"
                gap={2}
                className="mx-auto justify-content-center TopupBreakdown"
              >
                {t("you_got")}{" "}
                <div className="BonusAmounts">
                  {!!bonusHpr && (
                    <p className="HprAmount">
                      {!isFiatDisplay && <HyperCoin width={13} height={13} />}
                      {displayBonusHpr}
                    </p>
                  )}
                  {hasHprAndGem && <>+</>}
                  {!!gem && (
                    <p className="GemAmount">
                      <Gem width={14.4} height={12.8} />
                      {gem}
                    </p>
                  )}
                </div>
                {t("extra")}!
              </Stack>
            )}
          </Stack>
        </div>

        <div className="d-flex align-items-end Cta">
          <PrimaryActionButton
            size="lg"
            text={t("games:game_results_page.go_to_games")}
            onClick={handleGoToGames}
          />
        </div>
      </div>
    </>
  );
}
